import { Card, type Column } from '@platform/design-system';
import type { ShippingClient, TypedClient } from '@platform/openapi';
import { namesOf, useGeoAreas } from '../geography/useGeoAreas.ts';
import type { ReportPeriod } from '../report-period.ts';
import { carrierNameOf, useCarriers } from '../settings/carriers.ts';
import { usePageContext } from '../usePageContext.ts';
import { GovernorateMap } from './GovernorateMap.tsx';
import { RateBar } from './RateBar.tsx';
import { QueryView } from '../QueryView.tsx';
import { deliveryRateColumn, numericColumn } from './columns.ts';
import { StatisticsTable } from './StatisticsTable.tsx';
import { StageTiles } from './StageTiles.tsx';
import { rateOf, useStatisticsFormat, useStatisticsReport, type DeliveryReport } from './statistics.ts';
import styles from './Statistics.module.css';

const PARCEL_STAGES = ['depot', 'transit', 'delivered', 'failed', 'returned'] as const;

type CarrierRow = DeliveryReport['carriers'][number];

export type DeliveryStatisticsProps = {
  api: TypedClient;
  shipping: ShippingClient;
  period: ReportPeriod;
  carrier: string | null;
};

const DeliveryFigures = ({
  report,
  shipping,
  api,
}: {
  report: DeliveryReport;
  shipping: ShippingClient;
  api: TypedClient;
}) => {
  const { t } = usePageContext();
  const format = useStatisticsFormat(report.currency);
  const carriers = useCarriers(shipping).data?.items;
  const geoNames = namesOf(useGeoAreas(api).data);
  const delivered = report.stages.delivered.count;
  const returned = report.stages.returned.count;
  const timed = report.delay.bands.reduce((total, band) => total + band, 0);
  const columns: readonly Column<CarrierRow>[] = [
    {
      id: 'carrier',
      header: t('statistics.carrier'),
      primary: true,
      cell: (row) => carrierNameOf(carriers, row.carrierCode),
    },
    numericColumn('parcels', t('statistics.parcels'), (row) => format.count(row.parcels)),
    deliveryRateColumn(t('statistics.deliveryRate'), format),
    numericColumn('days', t('statistics.averageDelay'), (row) => format.days(row.averageDays)),
    numericColumn('owed', t('statistics.owed'), (row) => format.money(row.owed)),
  ];
  return (
    <div className={styles['stack']}>
      <StageTiles
        stages={report.stages}
        order={PARCEL_STAGES}
        labelPrefix="statistics.parcel_"
        format={format}
        loading={false}
      />
      <div className={styles['columns']}>
        <Card title={t('statistics.delayTitle')}>
          <p className={styles['headline']}>
            <strong>{format.days(report.delay.averageDays)}</strong>
            <span className={styles['muted']}>{t('statistics.delayHelp')}</span>
          </p>
          {report.delay.bands.map((band, index) => (
            <RateBar
              key={String(index + 1)}
              label={t(`statistics.delayBand_${String(index + 1)}`)}
              value={rateOf(band, timed)}
              text={format.share(rateOf(band, timed))}
            />
          ))}
        </Card>
        <Card title={t('statistics.performanceTitle')}>
          <p className={styles['muted']}>
            {t('statistics.performanceHelp', { parcels: format.count(delivered + returned) })}
          </p>
          <RateBar
            label={t('statistics.parcel_delivered')}
            value={rateOf(delivered, delivered + returned)}
            text={format.rate(rateOf(delivered, delivered + returned))}
          />
          <RateBar
            label={t('statistics.parcel_returned')}
            value={rateOf(returned, delivered + returned)}
            text={format.rate(rateOf(returned, delivered + returned))}
            tone="warning"
          />
        </Card>
      </div>
      <StatisticsTable
        title={t('statistics.carriersTitle')}
        columns={columns}
        rows={report.carriers}
        rowKey={(row) => row.carrierCode}
        emptyTitle={t('statistics.noParcels')}
      />
      <GovernorateMap
        governorates={report.governorates}
        nameOf={geoNames}
        format={format}
        carrierNameOf={(code) => carrierNameOf(carriers, code)}
      />
    </div>
  );
};

export const DeliveryStatistics = ({ api, shipping, period, carrier }: DeliveryStatisticsProps) => {
  const { t } = usePageContext();
  const query = useStatisticsReport('delivery', period, { carrier }, () =>
    api.get('/admin/statistics/delivery', { query: { ...period, ...(carrier === null ? {} : { carrier }) } }),
  );
  return (
    <QueryView query={query} label={t('statistics.tab_delivery')}>
      {(report) => <DeliveryFigures report={report} shipping={shipping} api={api} />}
    </QueryView>
  );
};
